// @ts-nocheck
import React, { useState, useEffect, useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import Router from 'next/router';
import { useAtom, useSetAtom } from 'jotai';
import SCDropdownList from '@/components/sc-controls/form-controls/sc-dropdownlist';
import SCInput from '@/components/sc-controls/form-controls/sc-input';
import SCPill from '@/components/sc-controls/form-controls/sc-pill';
import Helper from '@/utils/helper';
import * as Enums from '@/utils/enums';
import Fetch from '@/utils/Fetch';
import Storage from '@/utils/storage';
import SubscriptionContext from '@/utils/subscription-context';
import ToastContext from '@/utils/toast-context';
import SCMessageBarContext from '@/utils/contexts/sc-message-bar-context';
import { justCompletedSetupAtom, forceInitialSetupAtom, confettiFireAtom } from '@/utils/atoms';
import { Title, Text, Group, Stack, Flex, Loader, Button, Box, SimpleGrid, Space } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { IconArrowRight, IconCircleCheck } from '@tabler/icons';

const benefits = [
    'Create quotes, invoices and purchase orders faster',
    'Efficient job and team management',
    'Easy scheduling',
    'Automated texts and emails',
    'Effective inventory management',
    'Comprehensive re-usable forms'
];

const inputProps = {
    styles: {
        label: {fontWeight: 600}
    },
    maw: 610,
    // h: 48
}

const underLineSVG = () => {
    return <svg width="100%" height="7" viewBox="0 0 232 7" fill="none" preserveAspectRatio="none" style={{ display: 'block' }} xmlns="http://www.w3.org/2000/svg">
        <g filter="url(#filter0_ng_57_719)">
            <rect x="1" y="1" width="230" height="5" fill="#51CB68"/>
        </g>
        <defs>
            <filter id="filter0_ng_57_719" x="0" y="0" width="232" height="7" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
                <feFlood flood-opacity="0" result="BackgroundImageFix"/>
                <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>
                <feTurbulence type="fractalNoise" baseFrequency="2 2" stitchTiles="stitch" numOctaves="3" result="noise" seed="8531" />
                <feColorMatrix in="noise" type="luminanceToAlpha" result="alphaNoise" />
                <feComponentTransfer in="alphaNoise" result="coloredNoise1">
                    <feFuncA type="discrete" tableValues="1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 "/>
                </feComponentTransfer>
                <feComposite operator="in" in2="shape" in="coloredNoise1" result="noise1Clipped" />
                <feFlood flood-color="rgba(0, 0, 0, 0.25)" result="color1Flood" />
                <feComposite operator="in" in2="noise1Clipped" in="color1Flood" result="color1" />
                <feMerge result="effect1_noise_57_719">
                    <feMergeNode in="shape" />
                    <feMergeNode in="color1" />
                </feMerge>
                <feTurbulence type="fractalNoise" baseFrequency="0.38461539149284363 0.38461539149284363" numOctaves="3" seed="1613" />
                <feDisplacementMap in="effect1_noise_57_719" scale="2" xChannelSelector="R" yChannelSelector="G" result="displacedImage" width="100%" height="100%" />
                <feMerge result="effect2_texture_57_719">
                    <feMergeNode in="displacedImage"/>
                </feMerge>
            </filter>
        </defs>
    </svg>

}

function SignUpIcp() {

    const subscriptionContext = useContext<any>(SubscriptionContext);
    const isMobileView = useMediaQuery('(max-width: 800px)');
    const toast = useContext<any>(ToastContext);
    const messageBar = useContext<any>(SCMessageBarContext);

    // The maintenance/integration notification banner is a fixed, full-width bar
    // (48px tall) rendered at the very top of the app. When it's active we leave
    // room at the top so it doesn't cover the logo. Subscription/trial notices are
    // suppressed on this page (see SCMessageBar), so they don't factor into the offset.
    const bannerActive = messageBar?.maintenanceActive === true || messageBar?.integrationErrorActive === true;
    const bannerOffset = bannerActive ? 48 : 0;

    const [justCompletedSetup, setJustCompletedSetup] = useAtom(justCompletedSetupAtom);
    const [forceInitialSetup, setForceInitialSetup] = useAtom(forceInitialSetupAtom);
    // confettiFireAtom is an object: increment `count` to fire the drop, and
    // pass a `message` to also show a centered welcome modal.
    const setConfettiFire = useSetAtom(confettiFireAtom);

    // Guard: if the company is already set up (and we're not forcing setup again),
    // don't keep the user on this page.
    useEffect(() => {
        const subscriptionInfo = subscriptionContext?.subscriptionInfo || Storage.getCookie(Enums.Cookie.subscriptionInfo);
        if (!forceInitialSetup && subscriptionInfo && subscriptionInfo.SetupComplete === true) {
            Helper.nextRouter(Router.replace, '/');
        }
    }, [subscriptionContext?.subscriptionInfo, forceInitialSetup]);

    const submitSetup = async (details: any) => {
        const result = await Fetch.post({
            url: '/Company/FinalizeSetup',
            params: details,
            toastCtx: toast
        } as any);

        if (result && result.status === 500) {
            // something went wrong — toast will automatically popup
            return false;
        }

        setJustCompletedSetup(true);
        setForceInitialSetup(false);

        await subscriptionContext.getSubscriptionInfo(true);
        return true;
    };

    const [pillItems, setPillItems] = useState([
        { label: 'Job Cards', selected: false },
        { label: 'Scheduling', selected: false },
        { label: 'Queries', selected: false },
        { label: 'Forms', selected: false },
        { label: 'Quotes', selected: false },
        { label: 'Invoices', selected: false },
        { label: 'Purchase Orders', selected: false },
        { label: 'Inventory', selected: false }
    ]);

    const [inputs, setInputs] = useState({
        Industry: null,
        IndustryOther: '',
        BusinessType: [],
        RoleInBusiness: null,
        InterestedFeatures: [],
        ReferredSource: null,
        CountryID: null,
        Tax: 15,
        EmployeeCount: null,
        UsingOtherSystem: null
    });

    const [submitting, setSubmitting] = useState(false);

    const [useTax] = useState(true);
    const [otherIndustryTypeSelected, setOtherIndustryTypeSelected] = useState(false);
    const employeeCountOptions = ['1-2', '3-5', '6-10', '11-20', '21-50', '51+'];
    const roleOptions = [
        'Business owner',
        'Administrator',
        'Technician/Artisan/Tradesman',
        'Accounts',
        'Sales Rep',
        'Driver',
        'Contractor',
        'Other'
    ];

    const [inputErrors, setInputErrors] = useState<any>({});

    const validate = () => {
        let validationItems = [{
            required: true, type: Enums.ControlType.Text, key: 'RoleInBusiness', value: selectedRole
        }, {
            required: true, type: Enums.ControlType.Text, key: 'Industry', value: selectedIndustry
        }, {
            required: true, type: Enums.ControlType.Text, key: 'BusinessType', value: selectedBusinessType
        }, {
            required: true, type: Enums.ControlType.Text, key: 'CountryID', value: selectedCountry
        }, {
            required: true, type: Enums.ControlType.Text, key: 'ReferredSource', value: inputs.ReferredSource
        }, {
            required: useTax, type: Enums.ControlType.Number, key: 'Tax', value: inputs.Tax
        }, {
            required: true, type: Enums.ControlType.Text, key: 'EmployeeCount', value: selectedEmployeeCount
        }];

        if (otherIndustryTypeSelected) {
            validationItems = [...validationItems, {
                required: true, type: Enums.ControlType.Text, key: 'IndustryOther', value: inputs.IndustryOther
            }];
        }

        const { isValid, errors } = Helper.validateInputs(validationItems);
        setInputErrors(errors);
        return isValid;
    };

    const doConfirm = async () => {
        if (validate()) {
            setSubmitting(true);

            const result = {
                ...inputs,
                Industry: selectedIndustry,
                EmployeeCount: selectedEmployeeCount,
                RoleInBusiness: selectedRole,
                CountryID: selectedCountry ? selectedCountry.ID : null,
                CurrencyID: selectedCurrency ? selectedCurrency.ID : null,
                Timezone: selectedTimezone ? selectedTimezone.Id : null
            };

            const success = await submitSetup(result);
            if (success) {
                setConfettiFire(p => ({ count: p.count + 1, message: 'Welcome to ServCraft!' }));
                Helper.nextRouter(Router.replace, '/');
            }
            setSubmitting(false);
        }
    };

    const [industryList, setIndustryList] = useState<any[]>([]);
    const [referredSourceList, setReferredSourceList] = useState<any[]>([]);

    const industryListQuery = useQuery({
        queryKey: ['industryList'],
        queryFn: () => Fetch.get({ url: '/Company/IndustryList' })
    });

    const referredSourceListQuery = useQuery({
        queryKey: ['referredSourceList'],
        queryFn: () => Fetch.get({ url: '/Company/ReferredSourceList' })
    });

    useEffect(() => {
        if (referredSourceListQuery.data && referredSourceListQuery.isSuccess) {
            const results = referredSourceListQuery.data.Results ?? [];
            const list = results.filter((x: any) => x !== 'Other');
            list.sort();
            if (results.some((x: any) => x === 'Other')) {
                list.push('Other');
            }
            setReferredSourceList(list);
        }
    }, [referredSourceListQuery.data, referredSourceListQuery.isSuccess]);

    useEffect(() => {
        if (industryListQuery.data && industryListQuery.data.Results) {
            let list = industryListQuery.data.Results?.filter((x: any) => x !== 'Other') ?? [];
            list.sort();
            list.push('Other');
            setIndustryList(list);
        } else {
            setIndustryList(['Other']);
        }
    }, [industryListQuery.data, industryListQuery.isSuccess]);

    const [selectedIndustry, setSelectedIndustry] = useState<any>();
    const [selectedRole, setSelectedRole] = useState<any>();
    const [selectedBusinessType, setSelectedBusinessType] = useState<any>();
    const [selectedReferredSource, setSelectedReferredSource] = useState<any>();

    const handleIndustryChange = (e: any) => {
        setSelectedIndustry(e);
        setInputs({ ...inputs, Industry: e });
    };

    const handleRoleChange = (e: any) => {
        setSelectedRole(e);
        setInputs({ ...inputs, RoleInBusiness: e });
        setInputErrors({ ...inputErrors, RoleInBusiness: '' });
    };

    const handleBusinessTypeChange = (e: any) => {
        setSelectedBusinessType(e);
        setInputs({ ...inputs, BusinessType: e });
        setInputErrors({ ...inputErrors, BusinessType: '' });
    };

    useEffect(() => {
        setOtherIndustryTypeSelected(selectedIndustry && selectedIndustry.toLowerCase() === 'other');
    }, [selectedIndustry]);

    const setIndustryOther = (e: any) => {
        setInputs({ ...inputs, IndustryOther: e.value });
    };

    const setInterestedFeatures = (val: any) => {
        setInputs({ ...inputs, InterestedFeatures: val.filter((x: any) => x.selected).map((x: any) => x.label) });
    };

    const [selectedEmployeeCount, setSelectedEmployeeCount] = useState<any>();

    const handleEmployeeCountChange = (option: any) => {
        setSelectedEmployeeCount(option);
        setInputs({ ...inputs, EmployeeCount: option });
        setInputErrors({ ...inputErrors, EmployeeCount: '' });
    };

    const handleReferredSourceChange = (option: any) => {
        setSelectedReferredSource(option);
        setInputs({ ...inputs, ReferredSource: option });
        setInputErrors({ ...inputErrors, ReferredSource: '' });
    };

    const [selectedCountry, setSelectedCountry] = useState<any>();
    const [selectedCurrency, setSelectedCurrency] = useState<any>(null);
    const [selectedTimezone, setSelectedTimezone] = useState<any>(null);

    const searchItems = async () => {
        const countriesRequest = await Fetch.get({ url: '/Country' });
        setSelectedCountry(countriesRequest.Results.find((x: any) => x.Description === 'South Africa'));

        const currenciesRequest = await Fetch.get({ url: '/Company/GetCurrencies' });
        setSelectedCurrency(currenciesRequest.Results.find((x: any) => x.Code === 'ZAR'));

        const timezonesRequest = await Fetch.get({ url: '/Company/GetTimezones' });
        setSelectedTimezone(timezonesRequest.Results.find((x: any) => x.Id === 'South Africa Standard Time'));
    };

    useEffect(() => {
        searchItems();
    }, []);

    // The HubSpot chat launcher is injected outside the React tree and can persist
    // (already loaded) from a page visited just before this one, overlapping the
    // "Get Started" button on mobile. Force it hidden for as long as this page is
    // mounted, and let it reappear once the user moves on.
    useEffect(() => {
        const hideChatWidget = () => {
            const elem = document.getElementById('hubspot-messages-iframe-container');
            if (elem?.style) {
                elem.style.setProperty('display', 'none', 'important');
            }
        };

        hideChatWidget();
        const interval = setInterval(hideChatWidget, 200);

        return () => {
            clearInterval(interval);
            const elem = document.getElementById('hubspot-messages-iframe-container');
            if (elem?.style) {
                elem.style.setProperty('display', 'block', 'important');
            }
        };
    }, []);

    const formGap = { base: 'xs', md: 'xl', lg: 32 };

    return (
        <Flex h={'100dvh'} w={'100%'} style={{ overflow: 'hidden' }} direction={{ base: 'column', md: 'row' }}>

            {/* ── Left: setup form ─────────────────────────────────────────── */}
            <Box
                w={{ base: '100%', md: '50%' }}
                h={'100dvh'}
                px={{ base: 'lg', md: 60 }}
                py={{ base: 10, md: 10, xl: 40 }}
                style={{ overflowY: 'auto' }}
            >
                {/* Leave room for the fixed notification banner so the logo isn't covered when it's active */}
                {bannerOffset ? <Space h={bannerOffset} /> : null}

                {/* Constrain the form so a single full-width field lines up with the two columns above it */}
                <Box mx={'auto'} maw={610}>

                    <img src={'/logo-type-blue.svg'} alt={'ServCraft'} style={{ height: 28 }} />
                    <Title size={28} mt={{base: 20, md: 0, lg: 15, xl: 40}} mb={{base: 'xl', md: 'sm', xl: 'xl'}}>
                        Tell us about your business
                    </Title>

                    <SimpleGrid cols={{ base: 1, md: 2 }} spacing={formGap} verticalSpacing={formGap}>
                        <SCDropdownList
                            mt={0}
                            error={inputErrors.RoleInBusiness}
                            label="What is your role in the business?"
                            options={roleOptions}
                            required={true}
                            onChange={handleRoleChange}
                            name="Role"
                            value={selectedRole}
                            targetInputProps={inputProps}
                        />
                        <SCDropdownList
                            mt={0}
                            error={inputErrors.EmployeeCount}
                            label="How many employees do you have?"
                            options={employeeCountOptions}
                            placeholder=""
                            required={true}
                            onChange={handleEmployeeCountChange}
                            name="EmployeeCount"
                            value={selectedEmployeeCount}
                            targetInputProps={inputProps}
                        />
                        <SCDropdownList
                            mt={0}
                            error={inputErrors.Industry}
                            label="What industry do you work in?"
                            options={industryList}
                            required={true}
                            onChange={handleIndustryChange}
                            name="Industry"
                            value={selectedIndustry}
                            targetInputProps={inputProps}
                        />
                        <SCDropdownList
                            mt={0}
                            error={inputErrors.BusinessType}
                            label="What type of service do you provide?"
                            options={[
                                'Field Service',
                                'Workshops',
                                'Field Service and Workshops',
                                'Other'
                            ]}
                            required={true}
                            onChange={handleBusinessTypeChange}
                            name="BusinessType"
                            value={selectedBusinessType}
                            targetInputProps={inputProps}
                        />
                    </SimpleGrid>

                    {otherIndustryTypeSelected ?
                        <SCInput
                            mt={formGap}
                            value={inputs.IndustryOther}
                            onChange={setIndustryOther}
                            label="Please specify your industry"
                            required={true}
                            error={inputErrors.IndustryOther}
                            targetInputProps={inputProps}
                        /> : ''
                    }

                    <SCDropdownList
                        mt={formGap}
                        error={inputErrors.ReferredSource}
                        label="How did you hear about ServCraft?"
                        options={referredSourceList}
                        required={true}
                        onChange={handleReferredSourceChange}
                        name="ReferredSource"
                        value={selectedReferredSource}
                        dropdownHeight={'min(35vh, 400px)'}
                        targetInputProps={{...inputProps, maw: '100%'}}
                    />

                    <Stack gap={1} mt={formGap}>
                        <Text size={'sm'} fw={600} c={'gray.9'} mb={3}>What features are you most interested in?</Text>
                        <Group>
                            <SCPill
                                style={{width: 110, textAlign: 'center', fontSize: '12px', paddingInline: 0}}
                                grow={isMobileView}
                                onChange={(val: any) => {
                                    setPillItems(val);
                                    setInterestedFeatures(val);
                                }}
                                items={pillItems}
                            />
                        </Group>
                    </Stack>

                    <Flex w={'100%'} justify={'end'} align={'center'} mt={{base: 30, xl: 40}} pb={{base: 'sm', xl: 'lg'}}>
                        <Button type={'submit'}
                                w={247}
                                radius={6}
                                fw={700}
                                style={{fontSize: '16px'}}
                                onClick={doConfirm}
                                size={'lg'}
                                color={'scBlue'}
                                rightSection={(submitting) && <Loader variant={'oval'} size={18} color={'white'} /> || <IconArrowRight size={16} />}
                                disabled={submitting}
                        >
                            {submitting ? 'Submitting' : 'Get Started'}
                        </Button>
                    </Flex>
                </Box>
            </Box>

            {/* ── Right: promo panel with blue overlay ─────────────────────── */}
            <Box
                visibleFrom={'md'}
                w={'50%'}
                h={'100dvh'}
                pos={'relative'}
                style={{
                    backgroundImage: 'url(/ICP/ICP-background.webp)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                }}
            >
                <Box
                    pos={'absolute'}
                    inset={0}
                    style={{
                        // Darker blue (~scBlue.7 = #0038c0) with a slightly lighter opacity
                        background: 'linear-gradient(180deg, rgba(0,56,192,0.82) 0%, rgba(0,37,185,0.82) 100%)'
                    }}
                />

                <Flex
                    pos={'relative'}
                    direction={'column'}
                    justify={'start'}
                    align={'center'}
                    h={'100%'}
                    px={{ base: 40, md: 60 }}
                    // Matches the left form's logo+title top offset (py + logo height + title mt) at each
                    // breakpoint, so the heading here stays level with "Tell us about your business" instead
                    // of drifting toward vertical center as the viewport grows.
                    mt={{ md: 38, lg: 53, xl: 108 }}
                    style={{ zIndex: 1 }}
                >
                    <Box mih={'65vh'} maw={600} w={'100%'}>
                        <Title c={'white'} order={1} size={38} mb={40} ta={'center'}>
                            Everything you need,&nbsp;
                            <span style={{ position: 'relative', display: 'inline-block', whiteSpace: 'nowrap' }}>
                                in one place
                                <span style={{ position: 'absolute', left: 0, right: 0, bottom: -2, lineHeight: 0 }}>
                                    {underLineSVG()}
                                </span>
                            </span>
                        </Title>

                        <Stack gap={'lg'}>
                            {benefits.map((benefit) => (
                                <Group key={benefit} gap={'sm'} wrap={'nowrap'} align={'center'}>
                                    <IconCircleCheck size={22} color={'rgba(0, 255, 47, 1)'} style={{ flexShrink: 0 }} />
                                    <Text c={'white'} fw={600} size={'md'}>{benefit}</Text>
                                </Group>
                            ))}
                        </Stack>
                    </Box>
                </Flex>
            </Box>
        </Flex>
    );
}

export default SignUpIcp;

export const Route = createFileRoute('/sign-up-icp')({ component: SignUpIcp });
